import {noop} from '@benchling/js-utils';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ArchiveStatus} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications, callWithDataModifications} from 'audit/DataModificationsModal';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {warnAndRedirectAfterInaccessibleItemOpen} from 'auth/capabilities/InaccessibleFeaturesActions';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize, redirectForDownload} from 'BenchlingUtil';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  STORABLE_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import * as EventConstants from 'constants/EventConstants';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {
  Container,
  ContainerSummary,
  GRID_CLASSES,
  GridSummary,
  GridType,
  Location,
  Storable,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import AppDispatcher from 'lib/Dispatcher';
import notifications, {Response as NotificationResponse} from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as FilterUtil from 'search/FilterUtil';
import {populateSearchParamWithDefaultSort} from 'search/getSortsOrDefault';
import {FILTERS, SearchParams, SearchResults, SearchType} from 'search/SearchConstants';
import {refreshChips} from 'ui-library/chips/useSmartChipQuery';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import makeDeferred, {Deferred} from 'util/makeDeferred';
import {capitalize, typeToName} from 'util/NameUtil';
import * as net from 'util/Net';
import {FetchParams} from 'util/sanitizeFetchParams';

import * as ContainableDAO from './ContainableDAO';
import * as ContainerDAO from './ContainerDAO';
import {ContainerPropertiesUpdate} from './ContainerDAO';
import * as GridDAO from './GridDAO';
import getCreateGridParams, {CreateGridPaneState} from './grids/getCreateGridParams';
import * as LocationDAO from './LocationDAO';
import * as SampleUtil from './SampleUtil';
import * as StorableDAO from './StorableDAO';
import {StorablesSearchResponse} from './StorableDAO';
import invalidateRegistryFolderOnCreate from './storables/invalidateRegistryFolderOnCreate';
import * as StorableAuth from './storables/StorableAuth';

type ListContainersResponse = ContainableDAO.ListContainersResponse;
type ListPlatesResponse = ContainableDAO.ListPlatesResponse;
type BulkFetchContainersResponse = ContainerDAO.BulkFetchContainersResponse;
type CreateContainerParam = ContainerDAO.CreateContainerParam;
type CreateContainersResponse = ContainerDAO.CreateContainersResponse;
type ExportToCSVResponse = ContainerDAO.ExportToCSVResponse;
type FetchContainerResponse = ContainerDAO.FetchContainerResponse;
type UpdateContainerContentResponse = ContainerDAO.UpdateContainerContentResponse;
type UpdateContainerParams = ContainerDAO.UpdateContainerParams;
type UpdateContainerResponse = ContainerDAO.UpdateContainerResponse;
type FetchGridResponse = GridDAO.FetchGridResponse;
type UpdateGridParams = GridDAO.UpdateGridParams;
type UpdateGridResponse = GridDAO.UpdateGridResponse;
type CreateLocationParams = LocationDAO.CreateLocationParams;
type FetchLocationResponse = LocationDAO.FetchLocationResponse;
type UpdateLocationParams = LocationDAO.UpdateLocationParams;
type BulkMoveResponse = StorableDAO.BulkMoveResponse;
type BulkMoveStorableData = StorableDAO.BulkMoveStorableData;
type FetchStorablesResponse = StorableDAO.FetchStorablesResponse;

export interface CancelablePromise<T> {
  responsePromise: Promise<T>;
  cancelPromise: Deferred<string>;
}

function getFriendlyOrGenericError(res: NotificationResponse): string {
  return notifications.getFriendlyError(res) ?? GENERIC_SHORT_ERROR_MESSAGE;
}

export async function bulkGetStorablesWithAncestors(storableIds: Array<string>): Promise<void> {
  const storables = await StorableDAO.bulkGetStorablesWithAncestors(storableIds);
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLES_BULK_FETCH_COMPLETED,
    storables,
  });
}

/**
 * Fetches storable summaries
 * @param {string} registryId The registry of the storables to fetch
 * @param {Object} options Additional search options
 * @returns {Promise} The fetch storables Promise. This is returned to plug into old components
 */
export function fetchStorableSummaries(
  registryId: string | undefined,
  options: FetchParams
): CancelablePromise<FetchStorablesResponse> {
  const createErrorResponse = (errorMessage: string | null): FetchStorablesResponse => ({
    storables: [],
    limit: 0,
    total: 0,
    storableSummaryExtras: {},
    error: errorMessage ?? 'Unexpected error',
  });

  const cancelPromise = makeDeferred<string>();
  const fetchStorablesPromise = StorableDAO.fetchStorables(registryId, options).catch((error) => {
    notifications.notifyError(error);
    return createErrorResponse(notifications.getFriendlyError(error));
  });

  const responsePromise = Promise.race([
    fetchStorablesPromise,
    cancelPromise.then((errorMessage: string) => createErrorResponse(errorMessage)),
  ]).then((response: FetchStorablesResponse) => {
    if (!response.error) {
      AppDispatcher.dispatch({
        actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
        storables: response.storables,
        storableSummaryExtras: response.storableSummaryExtras,
      });
    }
    return response;
  });
  return {responsePromise, cancelPromise};
}

export async function bulkGetStorableSummaryExtras(storableIds: Array<string>): Promise<void> {
  const storableSummaryExtras = await StorableDAO.bulkGetStorableSummaryExtras(storableIds);
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
    storables: [],
    storableSummaryExtras,
  });
}

/**
 * List all storables summaries under a listed storable id
 */
export function listStorableSummaries(
  registryId: string | undefined,
  storableTypes: Array<StorableItemType>,
  listedStorableId: string | null,
  includeArchived: boolean = true,
  offset: number = 0
): CancelablePromise<FetchStorablesResponse> {
  let queryFilter: string;
  const baseFilters = [
    {key: FILTERS.TYPES.key, value: storableTypes},
    ...(includeArchived ? [] : [{key: FILTERS.ARCHIVE_STATUS.key, value: ArchiveStatus.NOT_ARCHIVED}]),
  ];
  if (listedStorableId != null) {
    queryFilter = FilterUtil.filtersToArgs([
      ...baseFilters,
      {key: FILTERS.PARENT_STORABLE.key, value: listedStorableId},
    ]).queryFilter;
  } else {
    queryFilter = FilterUtil.filtersToArgs(baseFilters).queryFilter;
  }
  return fetchStorableSummaries(registryId, {filter: queryFilter, sort: {offset}});
}

export function openLocationExpandedSearch(location: {
  id: string;
  storableSchema: {registryId: string};
}): void {
  // Extra check here because locations are not opened through FileBrowserActions.openFile()
  const status = getFeatureStatus({feature: InventoryFeature.INVENTORY_OPEN});
  if (!status.enabled) {
    warnAndRedirectAfterInaccessibleItemOpen(location.id, status.userMessage);
    return;
  }
  // eslint-disable-next-line global-require
  const SidepanelReduxActions = require('nav/sidepanel/SidepanelReduxActions');
  getStore().dispatch(SidepanelReduxActions.listLocation(location.id, location.storableSchema.registryId));
}

export function fetchLocationSummaries(
  registryId: string,
  limit: number | null = null
): CancelablePromise<FetchStorablesResponse> {
  const {queryFilter} = FilterUtil.filtersToArgs([
    {key: FILTERS.TYPES.key, value: [ModelTypeConstants.LOCATION]},
  ]);
  const options: FetchParams = {
    filter: queryFilter,
  };
  if (limit != null) {
    options.sort = {limit};
  }
  return fetchStorableSummaries(registryId, options);
}

export function fetchLocation(locationId: string): void {
  // TODO(TypeScript): Use regular promises so we don't need the explicit done callback type.
  LocationDAO.fetchLocation(locationId)
    .done(({location, ancestralLocations}: FetchLocationResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_FETCH_COMPLETED,
        location,
        ancestralLocations,
      });
    })
    .fail(notifications.notifyError);
}

export function open(storable: {id: string; name: string; storableSchema: {registryId: string}}): void {
  stubbableFunctions.doOpen(storable);
}

export async function fetchGrid(gridId: string): Promise<void> {
  try {
    const {grid, parentStorable}: FetchGridResponse = await GridDAO.fetch(gridId);
    AppDispatcher.dispatch({
      actionType: EventConstants.GRID_FETCH_COMPLETED,
      grid,
      parentStorable,
    });
  } catch (response) {
    if (!net.isServerRequestError(response)) {
      throw response;
    }
    if (StorableAuth.isUnreadableSampleError(response)) {
      StorableAuth.dispatchUnreadableSampleError(gridId, response);
    }
    if (response.status === 404) {
      StorableAuth.dispatchInaccessibleError(gridId, response);
    } else {
      notifications.notifyError(response);
    }
  }
}

export function invalidateGrid(gridId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.GRID_INVALIDATED,
    gridId,
  });
}

export function closeGrid(gridId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.GRID_CLOSE,
    gridId,
  });
}

export async function fetchContainer(containerId: string, notifyOnAuthError: boolean = false): Promise<void> {
  try {
    const {batches, container, containedBioentities, parentStorable}: FetchContainerResponse =
      await ContainerDAO.fetch(containerId);
    AppDispatcher.dispatch({
      actionType: EventConstants.CONTAINER_FETCH_COMPLETED,
      batches,
      container,
      parentStorable,
    });
    getStore().dispatch(ModelActions.filesFetchCompleted(_.indexBy(containedBioentities, 'id')));
  } catch (response) {
    if (!net.isServerRequestError(response)) {
      throw response;
    }
    // For some auth errors, it's better to handle it by reading the auth state and rendering an error banner
    // of some sort rather than intrusively popping up a toast.
    let isAuthError = true;
    if (StorableAuth.isUnreadableSampleError(response)) {
      StorableAuth.dispatchUnreadableSampleError(containerId, response);
    } else if (response.status === 404) {
      StorableAuth.dispatchInaccessibleError(containerId, response);
    } else {
      isAuthError = false;
    }
    if (notifyOnAuthError || !isAuthError) {
      notifications.notifyError(response);
    }
  }
}

export async function bulkFetchContainers(
  containerIds: Array<string>,
  silentlyFilterOutInaccessibleContainers: boolean = false
): Promise<BulkFetchContainersResponse> {
  // TODO(TypeScript): Switch to native promises to make typechecking cleaner here.
  const {containers, batchesById, fileSummariesById} = (await ContainerDAO.bulkFetch(
    containerIds,
    silentlyFilterOutInaccessibleContainers
  )) as BulkFetchContainersResponse;
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINER_BULK_FETCH_COMPLETED,
    containersById: _.indexBy(containers, 'id'),
    batchesById,
  });
  getStore().dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
  return {containers, batchesById, fileSummariesById};
}

export function dispatchStorablesLoaded(storables: Array<Storable>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLES_BULK_FETCH_COMPLETED,
    storables,
  });
}

export async function bulkFetchStorables(storableIds: Array<string>): Promise<void> {
  const {storables} = await StorableDAO.bulkFetchStorables(storableIds);
  dispatchStorablesLoaded(storables);
}

export function dispatchStorableSummariesLoaded(
  storableSummaries: Array<StorableSummary>,
  storableSummaryExtras?: StorableSummaryExtras
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
    storables: storableSummaries,
    storableSummaryExtras,
  });
}

export async function bulkFetchSummaries(storableIds: Array<string>): Promise<Array<StorableSummary>> {
  const {storableSummaries} = await StorableDAO.bulkFetchSummaries(storableIds);
  dispatchStorableSummariesLoaded(storableSummaries);
  return storableSummaries;
}

/*
 * Fetch transfers to and from container.
 */
export async function fetchContainerTransferInfo(containerId: string): Promise<void> {
  const {inputTransfers, outputTransfers} = await ContainerDAO.fetchTransfers(containerId);
  // eslint-disable-next-line no-restricted-syntax
  const inputContainers: Array<ContainerSummary> = _.compact(_.pluck(inputTransfers, 'inputContainer'));
  // eslint-disable-next-line no-restricted-syntax
  const outputContainers: Array<ContainerSummary> = _.pluck(outputTransfers, 'outputContainer');
  const allContainers = inputContainers.concat(outputContainers);
  // Input and output containers are summaries.
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
    storables: allContainers,
  });

  const parentPlateIds = SampleUtil.getParentPlateIds(allContainers);

  // Return the storables that were transferred in and out of the container. For each input and output
  // container, it's the container if it's not contained in a plate, or the parent plate if it is,
  // because users refer to plate wells by their parent plates instead of their containers.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const containersToPlateOrContainerIdsFn = (containers: Array<ContainerSummary>) =>
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(
      containers.map(({id, parentStorableId}) =>
        parentStorableId && parentPlateIds.includes(parentStorableId) ? parentStorableId : id
      )
    );
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINER_FETCH_TRANSFER_INFO_COMPLETED,
    containerId,
    containerTransferInfo: {
      inputStorableIds: containersToPlateOrContainerIdsFn(inputContainers),
      outputStorableIds: containersToPlateOrContainerIdsFn(outputContainers),
    },
  });

  // If containers are contained in plates, fetch those parent plates.
  if (parentPlateIds.length) {
    await bulkFetchSummaries(parentPlateIds);
  }
}

export function updateContainerWithDataMods(
  containerId: string,
  containerFields: UpdateContainerParams,
  onFinish?: (errorMessage?: string) => void,
  /* Callback for when the update does not go through; either because data mods is cancelled or an error is
   * raised. Useful for reverting optimistic updates. */
  onAbort?: () => void
): void {
  const modifiedAction = containerFields.parentStorableData
    ? InventoryRegistryDataModAction.MOVE
    : InventoryRegistryDataModAction.MODIFY;
  const folderChangeParams =
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY && containerFields.sourceId != null
      ? {apiIds: [containerId], destinationFolderApiId: containerFields.sourceId}
      : undefined;
  callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: [containerId],
      action: modifiedAction,
    }),
    onPerformAction: (dataModsHeader?) => {
      updateContainer(containerId, containerFields, onFinish, onAbort, dataModsHeader);
    },
    onModalCancel: () => {
      onAbort?.();
    },
    apiIds: [containerId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
    useFolderMoveDataModsLogic: folderChangeParams,
  });
}

export function updateContainer(
  containerId: string,
  containerFields: UpdateContainerParams,
  onFinish?: (errorMessage?: string) => void,
  onAbort?: () => void,
  dataModsHeader?: DataModificationsHeader
): void {
  // TODO(TypeScript): Use regular promises so we don't need the explicit done callback type.
  ContainerDAO.updateContainer(containerId, containerFields, dataModsHeader)
    .done(({container, parentStorable}: UpdateContainerResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_UPDATE_COMPLETED,
        container,
        parentStorable,
      });
      const parentStorableId = SampleUtil.getNearestAncestralLocationId(parentStorable);
      if (parentStorableId) {
        fetchLocation(parentStorableId);
      }
      onFinish?.();
      notifications.success(`${container.name} successfully updated`);
    })
    .fail((response) => {
      if (onFinish != null) {
        const errorMessage = notifications.getFriendlyError(response);
        if (!errorMessage) {
          // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
          // encountered an unexpected error.
          notifications.notifyError(response);
        }
        onFinish(getFriendlyOrGenericError(response));
      } else {
        notifications.notifyError(response);
      }
      onAbort?.();
    });
}

export async function deleteContainerContentsWithDataMods(
  containerIds: Array<string>,
  onSuccess?: () => void
): Promise<void> {
  const changeSummary = getInventoryRegistryChangeSummary({
    apiIds: containerIds,
    action: InventoryRegistryDataModAction.MODIFY,
  });
  await callAsyncWithDataModifications({
    changeSummary,
    onPerformAction: (dataModsHeader?) => {
      deleteContainerContents(containerIds, onSuccess, dataModsHeader);
    },
    apiIds: containerIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: containerIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export async function deleteContainerContents(
  containerIds: Array<string>,
  onSuccess?: () => void,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  LoadingScreen.show('Removing contents...');
  try {
    const {invalidatableBatchIds, invalidatableContainedItemIds, invalidatableStorableIds} =
      await ContainerDAO.deleteContainerContents(containerIds, dataModsHeader);
    notifications.success('Removed contents from containers successfully.');
    onSuccess?.();
    AppDispatcher.dispatch({
      actionType: EventConstants.CONTAINER_CONTENTS_DELETE_COMPLETED,
      invalidatableBatchIds,
      invalidatableContainedItemIds,
      invalidatableStorableIds,
    });
  } finally {
    LoadingScreen.hide();
  }
}

export function updateContainerContentWithDataMods(
  containerId: string,
  containedItemId: string,
  concentration: LegacyMeasurement
): void {
  const changeSummary = getInventoryRegistryChangeSummary({
    apiIds: [containerId],
    action: InventoryRegistryDataModAction.MODIFY,
  });
  callWithDataModifications({
    changeSummary,
    onPerformAction: (dataModsHeader?) => {
      updateContainerContent(containerId, containedItemId, concentration, dataModsHeader);
    },
    apiIds: [containerId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function updateContainerContent(
  containerId: string,
  containedItemId: string,
  concentration: LegacyMeasurement,
  dataModsHeader?: DataModificationsHeader
): void {
  ContainerDAO.updateContainerContent(containerId, containedItemId, concentration, dataModsHeader)
    .done(({containerContent, gridId}: UpdateContainerContentResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_CONTENT_UPDATE_COMPLETED,
        containerContents: [containerContent],
        gridId,
      });
      notifications.success(`Concentration successfully updated`);
    })
    .fail(notifications.notifyError);
}

export function exportContainersToCSV(containerIds: Array<string>): void {
  const notificationId = _.uniqueId('export-containers-to-csv-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  ContainerDAO.exportToCSV(containerIds)
    .done((response: ExportToCSVResponse) => {
      redirectForDownload(response.url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .fail((response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function exportGridToCSV(gridId: string): void {
  const notificationId = _.uniqueId('export-grid-to-csv-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  GridDAO.exportToCSV([gridId])
    .then((response: ExportToCSVResponse) => {
      redirectForDownload(response.url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .catch((response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

/*
 * Extract args from `CreatePlatePane` that will be passed to the server when creating a new grid
 */

export function createGrid(
  createGridPaneState: CreateGridPaneState,
  onFinish: (errorMessage?: string) => void = noop,
  onSuccess?: (grid: GridSummary) => void
): void {
  const createGridParams = getCreateGridParams(createGridPaneState);

  GridDAO.createGrid(
    createGridParams.gridSchemaId,
    createGridParams.name,
    // eslint-disable-next-line no-restricted-syntax
    _.omit(createGridParams, 'gridSchemaId', 'name')
  )
    .done((grid: GridSummary) => {
      invalidateRegistryFolderOnCreate(grid);
      if (onSuccess) {
        onSuccess(grid);
      }
      onFinish();
      const gridType = APIUtil.apiIdToType(grid.id);
      notifications.success(`${capitalize(typeToName(gridType))} successfully created`);
    })
    .fail((response) => {
      const errorMessage = notifications.getFriendlyError(response);
      if (!errorMessage) {
        // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
        // encountered an unexpected error.
        notifications.notifyError(response);
      }
      onFinish(getFriendlyOrGenericError(response));
    });
}

export function createPlate(
  createPlatePaneState: CreateGridPaneState,
  onFinish?: (errorMessage?: string) => void
): void {
  createGrid(createPlatePaneState, onFinish, open);
}

export function createBox(
  createBoxPaneState: CreateGridPaneState,
  onFinish?: (errorMessage?: string) => void
): void {
  createGrid(createBoxPaneState, onFinish, open);
}

export async function updateGridWithDataMods(
  gridId: string,
  folderId: string | null,
  updateGridParams: UpdateGridParams = {},
  onFinish?: (errorMessage?: string) => void
): Promise<GridSummary | null> {
  const action =
    updateGridParams.name || updateGridParams.barcode || updateGridParams.sourceId || updateGridParams.tags
      ? InventoryRegistryDataModAction.MODIFY
      : InventoryRegistryDataModAction.MOVE;
  const changeSummary = getInventoryRegistryChangeSummary({apiIds: [gridId], action});

  let grid: GridSummary | null = null;
  const folderChangeParams =
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY && updateGridParams.sourceId != null
      ? {apiIds: [gridId], destinationFolderApiId: updateGridParams.sourceId}
      : undefined;
  await callAsyncWithDataModifications({
    changeSummary,
    onPerformAction: async (dataModsHeader?) => {
      grid = await updateGrid(gridId, folderId, updateGridParams, onFinish, dataModsHeader);
    },
    apiIds: [gridId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: action,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
    useFolderMoveDataModsLogic: folderChangeParams,
  });
  return grid;
}

export async function updateGrid(
  gridId: string,
  folderId: string | null,
  {name, barcode, parentStorableData, sourceId, tags}: UpdateGridParams = {},
  onFinish?: (errorMessage?: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<GridSummary | null> {
  AppDispatcher.dispatch({
    actionType: EventConstants.GRID_UPDATE_STARTED,
    gridId,
  });
  const toastId = 'update-grid-toast';
  try {
    notifications.info('Updating grid...', {id: toastId, duration: 0});
    const response: UpdateGridResponse = await GridDAO.updateGrid(
      gridId,
      folderId,
      {
        name,
        barcode,
        parentStorableData,
        sourceId,
        tags,
      },
      dataModsHeader
    );
    const {grid, parentStorable} = response;
    const gridType = APIUtil.isBox(grid.id) ? 'Box' : APIUtil.isPlate(grid.id) ? 'Plate' : 'Grid';
    AppDispatcher.dispatch({
      actionType: EventConstants.GRID_UPDATE_COMPLETED,
      // Add `sourceId` because endpoint only returns grid summary, which doesn't include `sourceId`
      grid: {sourceId, ...grid},
      parentStorable,
    });
    notifications.success(`${gridType} successfully updated`, {id: toastId});
    onFinish?.();
    return grid;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (response) {
    AppDispatcher.dispatch({
      actionType: EventConstants.GRID_UPDATE_FAILED,
      gridId,
    });
    if (onFinish != null) {
      const errorMessage = notifications.getFriendlyError(response);
      if (!errorMessage) {
        // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
        // encountered an unexpected error.
        notifications.notifyError(response, {id: toastId});
      }
      onFinish(getFriendlyOrGenericError(response));
    } else {
      notifications.notifyError(response, {id: toastId});
    }
  }
  return null;
}

/**
 * Validate the wells for creating a matrix plate
 */
export function validateWellSpreadsheet(
  gridId: string,
  gridSchemaId: string,
  containerSchemaId: string,
  spreadsheetName: string,
  spreadsheetURL: string,
  onFinish: (errorMessage?: string) => void = noop
): void {
  GridDAO.validateWellSpreadsheet(gridId, gridSchemaId, containerSchemaId, spreadsheetName, spreadsheetURL)
    .done(() => {
      onFinish();
    })
    .fail((response) => {
      const errorMessage = notifications.getFriendlyError(response);
      if (!errorMessage) {
        // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
        // encountered an unexpected error.
        notifications.notifyError(response);
      }
      onFinish(getFriendlyOrGenericError(response));
    });
}

export function createLocation(
  locationSchemaId: string,
  name: string,
  data: CreateLocationParams = {},
  onFinish: (errorMessage?: string) => void = noop
): void {
  LocationDAO.createLocation(locationSchemaId, name, data)
    .done((location) => {
      invalidateRegistryFolderOnCreate(location!);
      onFinish();
      notifications.success('Location successfully created');
    })
    .fail((response) => {
      onFinish(getFriendlyOrGenericError(response));
    });
}

export async function updateLocationWithDataMods(
  locationId: string,
  updateLocationParams: UpdateLocationParams,
  onStart?: () => void,
  onFinish?: (errorMessage?: string) => void
): Promise<Location | null> {
  const action = updateLocationParams.parentStorableData
    ? InventoryRegistryDataModAction.MOVE
    : InventoryRegistryDataModAction.MODIFY;
  const changeSummary = getInventoryRegistryChangeSummary({apiIds: [locationId], action});

  let location: Location | null = null;
  await callAsyncWithDataModifications({
    changeSummary,
    onPerformAction: async (dataModsHeader?) => {
      location = await updateLocation(locationId, updateLocationParams, onStart, onFinish, dataModsHeader);
    },
    apiIds: [locationId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: action,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
  return location;
}

export async function updateLocation(
  locationId: string,
  updateLocationParams: UpdateLocationParams,
  onStart?: () => void,
  onFinish?: (errorMessage?: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<Location | null> {
  onStart?.();
  try {
    const location: Location = (await LocationDAO.updateLocation(
      locationId,
      updateLocationParams,
      dataModsHeader
    ))!;
    onFinish?.();
    AppDispatcher.dispatch({
      actionType: EventConstants.LOCATION_FETCH_COMPLETED,
      location,
    });
    notifications.success('Location successfully updated');
    return location;
  } catch (response) {
    if (onFinish != null) {
      onFinish(getFriendlyOrGenericError(response));
    } else {
      throw response;
    }
  }
  return null;
}

export function createContainers(
  containerDatas: Array<CreateContainerParam>,
  {
    onCreate,
    onFinish = noop,
  }: {
    onCreate?: (containers: Array<Container>) => void;
    onFinish?: (errorMessage?: string) => void;
  } = {}
): void {
  ContainerDAO.createContainers(containerDatas)
    .done(({containers}: CreateContainersResponse) => {
      containers.forEach((container) => {
        invalidateRegistryFolderOnCreate(container);
      });
      if (onCreate) {
        onCreate(containers);
      }

      if (containers.length === 1) {
        open(containers[0]);
      }

      // Invalidate parent grids
      // @ts-expect-error - TS doesn't know that we filter out falsey parentStorableIds
      const parentGridIds: Array<string> = _.chain(containerDatas)
        .map((cd) => cd.parentStorableData.id)
        .filter((parentStorableId) => {
          let parentStorableType: GridType | undefined;
          if (parentStorableId) {
            const typeFromApiId = APIUtil.apiIdToType(parentStorableId) as unknown as GridType;
            parentStorableType = typeFromApiId;
          }
          return (
            parentStorableType !== undefined &&
            Boolean(parentStorableId) &&
            GRID_CLASSES.includes(parentStorableType as unknown as GridType)
          );
        })
        .uniq()
        .value();
      parentGridIds.forEach((parentGridId) => {
        invalidateGrid(parentGridId);
      });
      onFinish();
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_BULK_FETCH_COMPLETED,
        containersById: _.indexBy(containers, 'id'),
      });
      notifications.success(`${pluralize(containerDatas.length, 'Container')} successfully created`);
    })
    .fail((response: Response) => {
      onFinish(getFriendlyOrGenericError(response));
    });
}

export function updateContainerTagsWithDataMods(containerId: string, newTags: TagUpdateObject): void {
  callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: [containerId],
      action: InventoryRegistryDataModAction.MODIFY,
    }),
    onPerformAction: (dataModsHeader?) => {
      updateContainerTags(containerId, newTags, dataModsHeader);
    },
    apiIds: [containerId],
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function updateContainerTags(
  containerId: string,
  newTags: TagUpdateObject,
  dataModsHeader?: DataModificationsHeader
): void {
  ContainerDAO.updateTags(containerId, newTags, dataModsHeader)
    .done((container: Container) => {
      notifications.success(`Tags on ${container.name} successfully updated`);
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_TAGS_UPDATE_COMPLETED,
        container,
      });
    })
    .fail(notifications.notifyError);
}

export function resetSavingState(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLE_STORE_RESET_SAVING_STATE,
  });
}

export function starStorables(storableIds: Array<string>): void {
  StorableDAO.starStorables(storableIds)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.STORABLES_UPDATED,
        storableIds,
        updates: {starred: true},
      });
    })
    .fail(notifications.notifyError);
}

export function unstarStorables(storableIds: Array<string>): void {
  StorableDAO.unstarStorables(storableIds)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.STORABLES_UPDATED,
        storableIds,
        updates: {starred: false},
      });
    })
    .fail(notifications.notifyError);
}

export function bulkMoveStorablesWithDataMods(
  storableDatas: Array<BulkMoveStorableData>,
  onSuccess?: () => void,
  onFinish?: (errorMessage?: string) => void
): void {
  const apiIds = storableDatas.map(({id}) => id);
  callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds,
      action: InventoryRegistryDataModAction.MOVE,
    }),
    onPerformAction: (dataModsHeader?) => {
      bulkMoveStorables(storableDatas, onSuccess, onFinish, dataModsHeader);
    },
    apiIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MOVE,
      numModifiedTotalItems: storableDatas.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function bulkMoveStorables(
  storableDatas: Array<BulkMoveStorableData>,
  onSuccess?: () => void,
  onFinish?: (errorMessage?: string) => void,
  dataModsHeader?: DataModificationsHeader
): void {
  StorableDAO.bulkMove(storableDatas, dataModsHeader)
    .done(({invalidatableStorableIds}: BulkMoveResponse) => {
      notifications.success('Inventory items successfully moved');
      refreshChips(invalidatableStorableIds);
      if (onSuccess) {
        onSuccess();
      }
      onFinish?.();

      AppDispatcher.dispatch({
        actionType: EventConstants.STORABLES_MOVE_COMPLETED,
        invalidatableStorableIds,
      });
    })
    .fail((response) => {
      if (onFinish != null) {
        const errorMessage = notifications.getFriendlyError(response);
        if (!errorMessage) {
          // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
          // encountered an unexpected error.
          notifications.notifyError(response);
        }
        onFinish(getFriendlyOrGenericError(response));
      } else {
        notifications.notifyError(response);
      }
    });
}

export interface ListContainableContainersOptions {
  includeStorableSummaryExtras?: boolean;
  archivePurposes?: Array<string>;
}

export function listContainableContainers(
  listingId: string,
  containableId: string,
  offset: number,
  limit: number,
  {includeStorableSummaryExtras, archivePurposes}: ListContainableContainersOptions = {}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINABLE_LIST_CONTAINERS_STARTED,
    containableId,
    listingId,
  });
  ContainableDAO.listContainers(containableId, {offset, limit, includeStorableSummaryExtras, archivePurposes})
    .done(({containers, total, totalWithHidden, storableSummaryExtras}: ListContainersResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINABLE_LIST_CONTAINERS_COMPLETED,
        listingId,
        containers,
        pagination: {
          offset,
          limit,
          total,
          totalWithHidden,
        },
        storableSummaryExtras,
      });
    })
    .fail((resp: Response) => {
      notifications.notifyError(resp);
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINABLE_LIST_CONTAINERS_FAILED,
        listingId,
      });
    });
}

export function listContainablePlates(
  listingId: string,
  containableId: string,
  offset: number,
  limit: number,
  {
    includeStorableSummaryExtras,
    archivePurposes,
  }: {includeStorableSummaryExtras?: boolean; archivePurposes?: Array<string>} = {}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINABLE_LIST_PLATES_STARTED,
    containableId,
    listingId,
  });
  ContainableDAO.listPlates(containableId, {offset, limit, includeStorableSummaryExtras, archivePurposes})
    .done(
      ({plates, total, totalWithHidden, plateSummaryExtras, storableSummaryExtras}: ListPlatesResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.CONTAINABLE_LIST_PLATES_COMPLETED,
          listingId,
          plates,
          pagination: {
            offset,
            limit,
            total,
            totalWithHidden,
          },
          plateSummaryExtras,
          storableSummaryExtras,
        });
      }
    )
    .fail((resp) => {
      notifications.notifyError(resp);
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINABLE_LIST_PLATES_FAILED,
        listingId,
      });
    });
}

export function clearContainerListing(listingId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINABLE_CLEAR_CONTAINER_LISTING,
    listingId,
  });
}

export function clearPlateListing(listingId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINABLE_CLEAR_PLATE_LISTING,
    listingId,
  });
}

function searchStorablesCompleteResponse(
  searchParams: SearchParams
): CancelablePromise<StorablesSearchResponse> {
  const createErrorResponse = (errorMessage: string): StorablesSearchResponse => ({
    ids: [],
    groupItemIds: [],
    total: 0,
    isExact: false,
    timedOut: false,
    correlationId: undefined,
    storableSummaries: [],
    storableSummaryExtras: {},
    error: errorMessage,
  });
  const cancelPromise = makeDeferred<string>();
  const searchParamsWithDefault = populateSearchParamWithDefaultSort(searchParams, SearchType.STORABLE);
  const searchStorablesPromise = StorableDAO.searchStorables(searchParamsWithDefault);

  const responsePromise = Promise.race([
    searchStorablesPromise,
    cancelPromise.then((errorMessage: string) => createErrorResponse(errorMessage)),
  ]).then((response: StorablesSearchResponse) => {
    if (!response.error) {
      AppDispatcher.dispatch({
        actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
        storables: response.storableSummaries,
        storableSummaryExtras: response.storableSummaryExtras,
      });
    }
    return {
      ids: response.ids,
      // This endpoint does not support the groupItemIds feature.
      groupItemIds: [],
      total: response.total,
      isExact: response.isExact,
      timedOut: response.timedOut,
      correlationId: response.correlationId,
      storableSummaries: response.storableSummaries,
      storableSummaryExtras: response.storableSummaryExtras,
      error: response.error,
      groups: response.groups,
      nextToken: response.nextToken,
    };
  });
  return {responsePromise, cancelPromise};
}

export interface StorableSummarySearchResponse extends SearchResults {
  storableSummaries: Array<StorableSummary>;
  storableSummaryExtras: StorableSummaryExtras;
  error?: string;
}

export function searchStorablesWithSummaries(
  searchParams: SearchParams
): CancelablePromise<StorableSummarySearchResponse> {
  const {responsePromise, cancelPromise} = searchStorablesCompleteResponse(searchParams);

  return {
    responsePromise: responsePromise.then((response: StorableSummarySearchResponse) => ({
      ids: response.ids,
      groupItemIds: response.groupItemIds,
      total: response.total,
      isExact: response.isExact,
      timedOut: response.timedOut,
      storableSummaries: response.storableSummaries,
      storableSummaryExtras: response.storableSummaryExtras,
      error: response.error,
      groups: response.groups,
      nextToken: response.nextToken,
    })),
    cancelPromise,
  };
}

export async function bulkUpdateTagsWithDataMods(
  storableIds: Array<string>,
  newTags: TagUpdateObject
): Promise<void> {
  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: storableIds,
      action: InventoryRegistryDataModAction.MODIFY,
    }),
    onPerformAction: (dataModsHeader?) => {
      bulkUpdateTags(storableIds, newTags, dataModsHeader);
    },
    apiIds: storableIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: storableIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export async function bulkUpdateTags(
  storableIds: Array<string>,
  newTags: TagUpdateObject,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const {storables} = await StorableDAO.bulkUpdateTags(storableIds, newTags, dataModsHeader);
  AppDispatcher.dispatch({
    actionType: EventConstants.STORABLES_BULK_FETCH_COMPLETED,
    storables,
  });
  notifications.success(`Successfully updated ${storableIds.length} storables.`);
}

export async function bulkUpdateContainerPropertiesWithDataMods(
  containerUpdates: Array<ContainerPropertiesUpdate>,
  analyticsMetadata?: AnalyticsMetadataForContext
): Promise<void> {
  const apiIds = containerUpdates.map((container) => container.id);
  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds,
      action: InventoryRegistryDataModAction.MODIFY,
      containersMayBeWells: true,
    }),
    onPerformAction: (dataModsHeader?) => {
      bulkUpdateContainerProperties(containerUpdates, analyticsMetadata, dataModsHeader);
    },
    apiIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: containerUpdates.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export async function bulkUpdateContainerProperties(
  containerUpdates: Array<ContainerPropertiesUpdate>,
  analyticsMetadata?: AnalyticsMetadataForContext,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const {containers} = await ContainerDAO.bulkUpdateContainerProperties(
    containerUpdates,
    analyticsMetadata,
    dataModsHeader
  );
  AppDispatcher.dispatch({actionType: EventConstants.CONTAINER_BULK_UPDATE_COMPLETED, containers});
  refreshChips(containerUpdates.map(({id}) => id));
  notifications.success(
    `Successfully updated ${containers.length} ${pluralize(
      containers.length,
      'container'
    )} or plate ${pluralize(containers.length, 'well')}.`
  );
}

export async function bulkUpdateContainerContentConcentrationsWithDataMods(
  containerContentsWithConcentrations: Array<ContainerDAO.UpdateContainerContentConcentrationsParam>
): Promise<void> {
  const apiIds = containerContentsWithConcentrations.map((container) => container.containerId);
  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds,
      action: InventoryRegistryDataModAction.MODIFY,
      containersMayBeWells: true,
      modifyContainedSamples: true,
    }),
    onPerformAction: (dataModsHeader?) => {
      bulkUpdateContainerContentConcentrations(containerContentsWithConcentrations, dataModsHeader);
    },
    apiIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: containerContentsWithConcentrations.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export async function bulkUpdateContainerContentConcentrations(
  containerContentsWithConcentrations: Array<ContainerDAO.UpdateContainerContentConcentrationsParam>,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const {containerContents} = await ContainerDAO.bulkUpdateContainerContentConcentrations(
    containerContentsWithConcentrations,
    dataModsHeader
  );
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINER_CONTENT_UPDATE_COMPLETED,
    containerContents,
  });
  notifications.success(
    `Successfully updated ${containerContents.length} ${pluralize(
      containerContents.length,
      'concentration'
    )}.`
  );
}

export const stubbableFunctions = {
  doOpen(storable: {id: string; name: string; storableSchema: {registryId: string}}): void {
    const type = APIUtil.apiIdToType(storable.id);
    if (type === ModelTypeConstants.LOCATION) {
      openLocationExpandedSearch(storable);
    } else {
      // eslint-disable-next-line global-require
      const FileBrowserActions = require('file-browser/FileBrowserActions').default;
      FileBrowserActions.openFile(storable.id, {folderItem: storable});
    }
    analytics.track(ANALYTICS_MODULE.SAMPLES, 'opened_storable');
    analytics.deprecatedTrackEvent(STORABLE_ANALYTICS_ACTIONS.OPENED_STORABLE);
  },
};
